import { FileTextOutlined, LinkOutlined } from "@ant-design/icons";
import {
  Collapse,
  Form,
  Input,
  message,
  Progress,
  RadioChangeEvent,
  Segmented,
  Select,
} from "antd";
import type { Rule } from "antd/es/form";
import { useForm, useWatch } from "antd/es/form/Form";
import TextArea from "antd/es/input/TextArea";
import { inferBusinessDetails } from "api/businesses";
import { omniboxRequest } from "api/user";
import CrawlWebsiteToggle from "components/Common/CrawlWebsiteToggle";
import { TaskInputForm } from "components/FileAndUrlInputTypes";
import ModalWithBorders from "components/ModalWithBorders";
import { useCategories } from "hooks/useCategoryCRUD";
import {
  ProgressUpdate,
  useBulkAddTopics,
} from "pages/Topics/hooks/useBulkAddTopics";
import { KeyboardEvent, useEffect, useState } from "react";
import { ensureUrlScheme, isValidUrl } from "utils/urlUtils";
import { InitialTopicData, parseAndValidateCsvText } from "../utils/csvParsing";

type CrawlScope = "page" | "site"; // Define the possible crawl scopes

// Reinstate the prefix constant
const CREATE_CATEGORY_PREFIX = "__CREATE__";

type AddTopicsFormProps = TaskInputForm & {
  type: "TEXT" | "URL";
  crawl?: boolean;
  text_content?: string;
  url?: string;
  categoryId?: string;
};

const BulkAddTopics = ({
  isOpen,
  close,
}: {
  close: () => void;
  isOpen: boolean;
}) => {
  const [form] = useForm<AddTopicsFormProps>();
  const { categories, isLoadingCategories } = useCategories();
  const [progressStatus, setProgressStatus] = useState<ProgressUpdate | null>(
    null
  );
  const [categorySearchText, setCategorySearchText] = useState("");
  const [crawlScope, setCrawlScope] = useState<CrawlScope>("page");

  const { isProcessing, processAndAddTopics, setIsProcessing } =
    useBulkAddTopics();

  const inputType = useWatch("type", form);
  const textContent = useWatch("text_content", form);

  // Calculate valid topics count for display
  let validTopicCount = 0;
  if (inputType === "TEXT" && textContent) {
    const { data, errors } = parseAndValidateCsvText(textContent);
    if (errors.length === 0) {
      validTopicCount = data.length;
    }
    // If there are errors, count remains 0
  }

  // Map categories for the Select dropdown, ensuring unique names
  const uniqueCategoryOptions = categories.reduce(
    (acc, cat) => {
      // Ensure category has a valid ID and name
      if (cat.id && cat.name) {
        const lowerCaseName = cat.name.toLowerCase();
        // Check if we haven't added this name yet
        if (!acc.addedNames.has(lowerCaseName)) {
          // Add the name to the set
          acc.addedNames.add(lowerCaseName);
          // Add the category option to the list
          acc.options.push({ value: cat.id, label: cat.name });
        }
      }
      return acc; // Return the accumulator
    },
    // Initial accumulator value
    {
      options: [] as { value: string; label: string }[],
      addedNames: new Set<string>(),
    }
  ).options; // Extract the options array from the final accumulator

  // Check if the current search text matches any existing category name (case-insensitive)
  // Use uniqueCategoryOptions for the check now
  const searchMatchesExisting =
    categorySearchText &&
    uniqueCategoryOptions.some(
      (opt) => opt.label.toLowerCase() === categorySearchText.toLowerCase()
    );

  // Dynamically add a "Create" option if search text is new
  // Base the dynamic options on the unique list
  const dynamicCategoryOptions = [
    ...uniqueCategoryOptions, // Start with unique options
    ...(categorySearchText && !searchMatchesExisting
      ? [
          {
            value: `${CREATE_CATEGORY_PREFIX}${categorySearchText}`,
            label: `Create new category "${categorySearchText}"`,
          },
        ]
      : []),
  ];

  const textRules = [
    {
      required: inputType === "TEXT",
      message: "Input is required.",
    },
    {
      validator: async (_: any, value: string | undefined) => {
        if (!value) {
          return Promise.resolve();
        }
        const { errors } = parseAndValidateCsvText(value);
        if (errors.length > 0) {
          const errorMsg = errors.join("\n");
          return Promise.reject(new Error(errorMsg));
        }
        return Promise.resolve();
      },
    },
  ];
  const urlRules: Rule[] = [
    { required: inputType === "URL", message: "URL is required." },
    {
      validator: async (_: any, value: string | undefined) => {
        if (!value) {
          return Promise.resolve();
        }
        if (isValidUrl(value)) {
          return Promise.resolve();
        } else {
          return Promise.reject(
            new Error(
              "Please enter a valid URL (e.g., example.com or https://example.com)"
            )
          );
        }
      },
    },
  ];

  const closeModal = () => {
    close();
    form.resetFields();
    setProgressStatus(null);
  };

  const handleProgressUpdate = (update: ProgressUpdate) => {
    setProgressStatus(update);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      form.submit();
    }
  };

  const handleFinish = async () => {
    setProgressStatus(null);

    if (inputType === "TEXT") {
      const textValue = form.getFieldValue("text_content");
      if (!textValue) {
        message.error("CSV text cannot be empty.");
        return;
      }

      const { data: parsedTopics, errors } = parseAndValidateCsvText(textValue);

      if (errors.length > 0) {
        errors.forEach((err) => message.error(err, 6));
        return;
      }

      if (parsedTopics && parsedTopics.length > 0) {
        const result = await processAndAddTopics(
          parsedTopics,
          handleProgressUpdate
        );
        if (result?.success) {
          closeModal();
        }
      } else {
        message.info("No valid CSV topics found in the text input.");
      }
    } else if (inputType === "URL") {
      const urlValue = form.getFieldValue("url");
      const selectedFormValue = form.getFieldValue("categoryId"); // Value from form

      if (!urlValue) {
        message.error("Please enter a URL.");
        return;
      }
      // Validation for category selection/input
      if (!selectedFormValue) {
        message.error("Please select or enter a category for the URL topics.");
        return;
      }

      let categoryIdForTopics: string | null = null;
      let categoryNameForTopics: string = "";

      // Check if the user intends to create a new category
      if (
        typeof selectedFormValue === "string" &&
        selectedFormValue.startsWith(CREATE_CATEGORY_PREFIX)
      ) {
        categoryNameForTopics = selectedFormValue.substring(
          CREATE_CATEGORY_PREFIX.length
        );
        categoryIdForTopics = null; // Mark for creation
        // Basic validation for the new name
        if (!categoryNameForTopics) {
          message.error("Cannot create a category with an empty name.");
          return;
        }
      } else {
        // User selected an existing category
        categoryIdForTopics = selectedFormValue;
        const selectedCategory = categories.find(
          (cat) => cat.id === categoryIdForTopics
        );
        if (!selectedCategory) {
          message.error(
            "Selected category not found. Please refresh and try again."
          );
          return;
        }
        categoryNameForTopics = selectedCategory.name;
      }

      // Use the crawlScope state to determine the crawl value
      const shouldCrawlSite = crawlScope === "site";

      setIsProcessing(true);
      try {
        message.info("Scraping URL content...");
        const processedUrl = ensureUrlScheme(urlValue);
        if (!isValidUrl(processedUrl)) {
          message.error("Invalid URL format detected.");
          return;
        }
        const scrapedData = await omniboxRequest({
          url: processedUrl,
          crawl: shouldCrawlSite, // Pass the boolean crawl value
        });

        if (!scrapedData?.content) {
          message.error("Could not extract content from the URL.");
          throw new Error("No content found from URL");
        }

        message.info("Extracting topics from content...");
        const inferredData = await inferBusinessDetails({
          content: scrapedData.content,
        });

        if (!inferredData?.topics || inferredData.topics.length === 0) {
          message.warning(
            "No topics could be automatically extracted from the URL content."
          );
          return;
        }

        const initialTopics: InitialTopicData[] = inferredData.topics.map(
          (topic) => ({
            topicName: topic.title,
            description: topic.body,
            // Use resolved category name and ID (which might be null for creation)
            categoryName: categoryNameForTopics,
            categoryId: categoryIdForTopics,
          })
        );

        const result = await processAndAddTopics(
          initialTopics,
          handleProgressUpdate
        );
        if (result?.success) {
          closeModal();
        }
      } catch (error: any) {
        console.error("Error processing URL:", error);
        message.error("An error occurred while processing the URL.");
        const shouldResetLoadingManually = !(
          error &&
          error.message &&
          typeof error.message === "string" &&
          error.message.includes("processAndAddTopics")
        );

        if (shouldResetLoadingManually) {
          setIsProcessing(false);
        }
      }
    }
  };

  const isOkDisabled = isLoadingCategories || isProcessing;

  useEffect(() => {
    if (inputType !== "URL" && form.getFieldValue("crawl")) {
      form.setFieldValue("crawl", undefined);
    } else if (
      inputType === "URL" &&
      form.getFieldValue("crawl") === undefined
    ) {
      form.setFieldValue("crawl", true);
    }
  }, [inputType, form]);

  const progressPercent = progressStatus
    ? progressStatus.total > 0
      ? Math.min(
          100,
          (progressStatus.current / (progressStatus.total + 1)) * 100
        )
      : progressStatus.current > 0
        ? 100
        : 0
    : 0;

  return (
    <ModalWithBorders
      title={
        <>
          <div>Bulk Import</div>
          <div className="text-sm text-[var(--antd-colorTextSecondary)] mt-1 font-normal">
            Import a batch of topics to your business.
          </div>
        </>
      }
      okButtonProps={{ loading: isProcessing, disabled: isOkDisabled }}
      onOk={() => form.submit()}
      onCancel={closeModal}
      okText="Import Topics"
      open={isOpen}
      destroyOnClose
    >
      <Form
        onFinish={handleFinish}
        id="create-topics-form"
        initialValues={{ type: "TEXT" }}
        layout="vertical"
        form={form}
        requiredMark={false}
        onKeyDown={handleKeyDown}
      >
        <Form.Item
          label="Input Type"
          name="type"
          rules={[{ required: true, message: "Please select an input type." }]}
        >
          <Segmented
            options={[
              {
                label: "CSV",
                value: "TEXT",
                icon: <FileTextOutlined />,
              },
              {
                label: "URL",
                value: "URL",
                icon: <LinkOutlined />,
              },
            ]}
            block
          />
        </Form.Item>
        <div>
          {inputType === "TEXT" && (
            <>
              <Form.Item
                label="CSV Formatted Topics"
                name="text_content"
                rules={textRules}
              >
                <TextArea
                  placeholder="Topic Name, Description, Category Name"
                  rows={5}
                  value={textContent}
                  onChange={(e) =>
                    form.setFieldsValue({ text_content: e.target.value })
                  }
                />
              </Form.Item>
              <div className="block mt-2 mb-4 text-[var(--antd-colorTextSecondary)]">
                Topics to add: {validTopicCount}
              </div>
              <Collapse ghost>
                <Collapse.Panel header="Format Instructions" key="1">
                  <div className="space-y-4 text-[var(--antd-colorTextSecondary)]">
                    <p>
                      Paste CSV formatted topics using this format:
                      <code className="block bg-[var(--antd-colorFillQuaternary)] p-2 rounded border border-[var(--antd-colorBorder)]">
                        Topic Name, Topic Description, Category Name
                      </code>
                    </p>
                    <p>
                      Categories will be created if they don't exist. If any
                      field contains a comma, enclose that field in quotes. ex.
                      "Topic, with comma"
                    </p>
                  </div>
                </Collapse.Panel>
              </Collapse>
            </>
          )}
          {inputType === "URL" && (
            <>
              <Form.Item
                label="Website URL"
                name="url"
                rules={urlRules}
                validateTrigger="onChange"
              >
                <Input placeholder="https://example.com" />
              </Form.Item>
              {
                <div className="mb-4">
                  <CrawlWebsiteToggle
                    checked={crawlScope === "site"}
                    handleChange={(e: RadioChangeEvent) =>
                      setCrawlScope(e.target.value ? "site" : "page")
                    }
                  />
                </div>
              }
              <Form.Item
                label="Target Category"
                name="categoryId"
                rules={[
                  {
                    required: inputType === "URL",
                    message: "Please select a category.",
                  },
                ]}
              >
                <Select
                  placeholder="Select or type to create a category"
                  options={dynamicCategoryOptions} // Use dynamic options
                  loading={isLoadingCategories}
                  disabled={isLoadingCategories}
                  showSearch
                  onSearch={setCategorySearchText} // Update search text state
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  // Allow selecting the "Create" option
                  optionFilterProp="label" // Important for selecting the dynamic option
                />
              </Form.Item>
              <div className="block mb-2 text-[var(--antd-colorTextSecondary)]">
                Topics extracted from the website URL will be added to the
                selected category. The website could be a blog, a landing page,
                or a competitors website you want to create content about.
              </div>
            </>
          )}
        </div>

        {isProcessing && progressStatus && (
          <div className="mt-6">
            <Progress
              percent={progressPercent}
              status="active"
              showInfo={false}
            />
            <div className="block text-center mt-2 text-[var(--antd-colorTextSecondary)]">
              {progressStatus.message}
            </div>
          </div>
        )}
      </Form>
    </ModalWithBorders>
  );
};

export default BulkAddTopics;
